import { FormEvent, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  CheckCircle2,
  CircleDollarSign,
  ClipboardList,
  MapPin,
  Pencil,
  Phone,
  Plus,
  Search,
  Trash2,
  UserRound,
  X,
} from "lucide-react";
import { adminApi } from "@/lib/api";
import type { BootstrapConfig, CrmEntity, CrmField, CrmRecord, TeamMember } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Textarea } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface EntityPageProps {
  config: BootstrapConfig;
}

const pipelineStages = [
  { id: "new", label: "New", tone: "border-sky-200 bg-sky-50 text-sky-800" },
  { id: "contacted", label: "Contacted", tone: "border-cyan-200 bg-cyan-50 text-cyan-800" },
  { id: "site_visit", label: "Site Visit", tone: "border-amber-200 bg-amber-50 text-amber-900" },
  { id: "quote_sent", label: "Quote Sent", tone: "border-orange-200 bg-orange-50 text-orange-900" },
  { id: "negotiation", label: "Negotiation", tone: "border-purple-200 bg-purple-50 text-purple-900" },
  { id: "won", label: "Won", tone: "border-emerald-200 bg-emerald-50 text-emerald-800" },
  { id: "installation", label: "Installation", tone: "border-teal-200 bg-teal-50 text-teal-800" },
  { id: "service", label: "Service", tone: "border-slate-200 bg-slate-50 text-slate-700" },
  { id: "lost", label: "Lost", tone: "border-stone-200 bg-stone-50 text-stone-700" },
];

const hiddenTaskFields = new Set(["related_type", "related_id"]);

function todayIsoDate(): string {
  const date = new Date();
  const offsetMs = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offsetMs).toISOString().slice(0, 10);
}

function emptyForm(entityName: string, entity: CrmEntity): Record<string, string> {
  const form = Object.fromEntries(entity.fields.map((field) => [field.name, ""]));
  if (entityName === "companies") form.status = "lead";
  if (entityName === "contacts") form.status = "new";
  if (entityName === "tasks") {
    form.status = "open";
    form.priority = "medium";
    form.due_date = todayIsoDate();
    form.linked_contact_id = "";
  }
  return form;
}

function formatLabel(value: unknown): string {
  return String(value ?? "").replace(/_/g, " ");
}

function formatMoney(value: unknown): string {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return "-";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(amount);
}

function recordValue(record: CrmRecord, fieldName: string): string {
  const value = record[fieldName] ?? record.custom?.[fieldName] ?? "";
  return String(value ?? "");
}

function entityDescription(entityName: string): string {
  if (entityName === "tasks") return "Create follow-ups, assign owners, and track due work.";
  if (entityName === "contacts") return "Track solar enquiries from first call to installation handoff.";
  if (entityName === "companies") return "Manage business accounts and solar project organizations.";
  if (entityName === "notes") return "Keep customer and service context in one place.";
  return "Manage shared business records.";
}

function isAssigneeField(field: CrmField): boolean {
  return field.name === "assignee" || field.name === "assigned_to";
}

function stageMeta(stage: unknown): (typeof pipelineStages)[number] {
  return pipelineStages.find((item) => item.id === String(stage)) ?? pipelineStages[0];
}

function dueTone(dueDate: unknown, status: unknown): string {
  if (String(status) === "completed") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  const due = String(dueDate ?? "");
  if (!due) return "border-slate-200 bg-slate-50 text-slate-600";
  const today = todayIsoDate();
  if (due < today) return "border-red-200 bg-red-50 text-red-700";
  if (due === today) return "border-amber-200 bg-amber-50 text-amber-800";
  return "border-teal-200 bg-teal-50 text-teal-700";
}

function dueLabel(dueDate: unknown, status: unknown): string {
  if (String(status) === "completed") return "Done";
  const due = String(dueDate ?? "");
  if (!due) return "No date";
  const today = todayIsoDate();
  if (due < today) return `Overdue ${due}`;
  if (due === today) return "Due today";
  return due;
}

function FieldInput({
  field,
  value,
  onChange,
  teamMembers,
  contacts,
}: {
  field: CrmField;
  value: string;
  onChange: (value: string) => void;
  teamMembers: TeamMember[];
  contacts: CrmRecord[];
}): import("react").ReactElement {
  if (field.name === "linked_contact_id") {
    const selected = value && !contacts.some((contact) => String(contact.id) === value);
    return (
      <select
        className="h-9 w-full rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">No contact linked</option>
        {selected && <option value={value}>Contact #{value}</option>}
        {contacts.map((contact) => (
          <option key={contact.id} value={String(contact.id)}>
            {recordValue(contact, "name")} {recordValue(contact, "phone") ? `- ${recordValue(contact, "phone")}` : ""}
          </option>
        ))}
      </select>
    );
  }

  if (isAssigneeField(field)) {
    const hasCurrentValue = value && !teamMembers.some((member) => member.name === value);
    return (
      <select
        className="h-9 w-full rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Unassigned</option>
        {hasCurrentValue && <option value={value}>{value}</option>}
        {teamMembers.map((member) => (
          <option key={member.id} value={member.name}>
            {member.name} ({formatLabel(member.role)})
          </option>
        ))}
      </select>
    );
  }

  if (field.type === "textarea") {
    return <Textarea value={value} onChange={(event) => onChange(event.target.value)} />;
  }
  if (field.type === "select") {
    return (
      <select
        className="h-9 w-full rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
        value={value}
        onChange={(event) => onChange(event.target.value)}
      >
        <option value="">Select</option>
        {field.options?.map((option) => <option key={option} value={option}>{formatLabel(option)}</option>)}
      </select>
    );
  }
  return (
    <Input
      type={field.type === "date" ? "date" : field.type === "number" ? "number" : "text"}
      value={value}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}

function PipelineBoard({
  contacts,
  selectedId,
  onSelect,
}: {
  contacts: CrmRecord[];
  selectedId?: number;
  onSelect: (record: CrmRecord) => void;
}): import("react").ReactElement {
  const activeStages = pipelineStages.filter((stage) => {
    if (stage.id === "lost") return contacts.some((contact) => recordValue(contact, "status") === "lost");
    return true;
  });

  return (
    <div className="overflow-x-auto pb-2">
      <div className="grid min-w-[1120px] grid-cols-9 gap-3">
        {activeStages.map((stage) => {
          const stageContacts = contacts.filter((contact) => recordValue(contact, "status") === stage.id);
          return (
            <section key={stage.id} className="min-h-44 rounded-md border bg-white/80">
              <div className="flex items-center justify-between border-b px-3 py-2">
                <span className="text-xs font-semibold uppercase text-muted-foreground">{stage.label}</span>
                <span className={cn("rounded-full border px-2 py-0.5 text-xs font-semibold", stage.tone)}>
                  {stageContacts.length}
                </span>
              </div>
              <div className="space-y-2 p-2">
                {stageContacts.slice(0, 8).map((contact) => (
                  <button
                    key={contact.id}
                    type="button"
                    onClick={() => onSelect(contact)}
                    className={cn(
                      "block w-full rounded-md border bg-white p-3 text-left shadow-sm transition hover:-translate-y-0.5 hover:shadow-md",
                      selectedId === contact.id && "border-primary ring-2 ring-primary/20",
                    )}
                  >
                    <div className="truncate text-sm font-semibold">{recordValue(contact, "name")}</div>
                    <div className="mt-1 flex items-center gap-1 truncate text-xs text-muted-foreground">
                      <Phone className="h-3 w-3" />
                      {recordValue(contact, "phone") || "No phone"}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="truncate text-xs text-muted-foreground">{recordValue(contact, "assigned_to") || "Unassigned"}</span>
                      <span className="text-xs font-semibold text-emerald-700">{formatMoney(recordValue(contact, "estimated_value"))}</span>
                    </div>
                  </button>
                ))}
                {stageContacts.length === 0 && (
                  <div className="rounded-md border border-dashed px-3 py-6 text-center text-xs text-muted-foreground">
                    Empty
                  </div>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

function Modal({
  title,
  children,
  onClose,
  size = "wide",
}: {
  title: string;
  children: import("react").ReactNode;
  onClose: () => void;
  size?: "wide" | "large";
}): import("react").ReactElement {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4 backdrop-blur-sm">
      <div
        className={cn(
          "max-h-[88vh] w-full overflow-hidden rounded-lg border bg-white shadow-2xl",
          size === "large" ? "max-w-5xl" : "max-w-3xl",
        )}
      >
        <div className="flex items-center justify-between gap-4 border-b px-5 py-4">
          <h2 className="text-base font-semibold">{title}</h2>
          <Button type="button" variant="ghost" onClick={onClose} className="h-8 w-8 px-0" aria-label="Close">
            <X className="h-4 w-4" />
          </Button>
        </div>
        <div className="max-h-[calc(88vh-65px)] overflow-y-auto p-5">{children}</div>
      </div>
    </div>
  );
}

export function EntityPage({ config }: EntityPageProps): import("react").ReactElement {
  const { entityName = "companies" } = useParams();
  const entity = config.crm.entities[entityName] ?? config.crm.entities.companies;
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState<CrmRecord | null>(null);
  const [selectedContact, setSelectedContact] = useState<CrmRecord | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [form, setForm] = useState<Record<string, string>>(() => emptyForm(entityName, entity));
  const [followUp, setFollowUp] = useState({ title: "", due_date: todayIsoDate(), priority: "medium", assignee: "" });
  const queryClient = useQueryClient();

  const records = useQuery({
    queryKey: ["crm", entityName, query],
    queryFn: () => adminApi.list(entityName, query),
  });

  const contactsQuery = useQuery({
    queryKey: ["crm", "contacts", "all"],
    queryFn: () => adminApi.list("contacts", ""),
  });

  const teamMembers = useQuery({
    queryKey: ["team-members", "entity-form"],
    queryFn: () => adminApi.listTeamMembers(""),
  });

  const contactSummary = useQuery({
    queryKey: ["contact-summary", selectedContact?.id],
    queryFn: () => adminApi.contactSummary(Number(selectedContact?.id)),
    enabled: entityName === "contacts" && Boolean(selectedContact?.id),
  });

  useEffect(() => {
    setEditing(null);
    setSelectedContact(null);
    setFormOpen(false);
    setDetailOpen(false);
    setForm(emptyForm(entityName, entity));
  }, [entityName, entity]);

  useEffect(() => {
    if (!selectedContact) return;
    setFollowUp({
      title: `Follow up with ${recordValue(selectedContact, "name")}`,
      due_date: recordValue(selectedContact, "next_follow_up_date") || todayIsoDate(),
      priority: "medium",
      assignee: recordValue(selectedContact, "assigned_to"),
    });
  }, [selectedContact]);

  const activeTeamMembers = useMemo(
    () => (teamMembers.data?.rows ?? []).filter((member) => member.active),
    [teamMembers.data?.rows],
  );
  const contactRows = contactsQuery.data?.rows ?? [];

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = Object.fromEntries(
        Object.entries(form).filter(([key, value]) => value !== "" && key !== "linked_contact_id"),
      );
      if (entityName === "tasks" && form.linked_contact_id) {
        payload.related_type = "contacts";
        payload.related_id = Number(form.linked_contact_id);
      }
      if (editing) return adminApi.update(entityName, editing.id, payload);
      return adminApi.create(entityName, payload);
    },
    onSuccess: async (result) => {
      setEditing(null);
      setFormOpen(false);
      setForm(emptyForm(entityName, entity));
      if (entityName === "contacts") setSelectedContact(result.record);
      await queryClient.invalidateQueries({ queryKey: ["crm", entityName] });
      await queryClient.invalidateQueries({ queryKey: ["crm", "contacts"] });
      await queryClient.invalidateQueries({ queryKey: ["crm-stats"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-summary"] });
    },
  });

  const createFollowUp = useMutation({
    mutationFn: async () => {
      if (!selectedContact) throw new Error("Select a contact first");
      return adminApi.create("tasks", {
        title: followUp.title,
        status: "open",
        priority: followUp.priority,
        due_date: followUp.due_date,
        assignee: followUp.assignee,
        related_type: "contacts",
        related_id: selectedContact.id,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["contact-summary", selectedContact?.id] });
      await queryClient.invalidateQueries({ queryKey: ["crm", "tasks"] });
      await queryClient.invalidateQueries({ queryKey: ["crm-stats"] });
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminApi.delete(entityName, id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["crm", entityName] });
      await queryClient.invalidateQueries({ queryKey: ["crm-stats"] });
    },
  });

  const visibleColumns = useMemo(() => {
    if (entityName === "tasks") {
      const preferred = ["title", "contact_name", "status", "priority", "due_date", "assignee"];
      return preferred.map((name) => ({
        name,
        label: name === "contact_name" ? "Contact" : entity.fields.find((field) => field.name === name)?.label ?? formatLabel(name),
        type: "text" as const,
      }));
    }
    if (entityName === "contacts") {
      const preferred = ["name", "phone", "status", "customer_type", "location", "system_size_kw", "estimated_value", "assigned_to", "next_follow_up_date"];
      return preferred
        .map((name) => entity.fields.find((field) => field.name === name))
        .filter((field): field is CrmField => Boolean(field));
    }
    return entity.fields.slice(0, 5);
  }, [entity.fields, entityName]);

  const formFields = useMemo(() => {
    const fields = entity.fields.filter((field) => !(entityName === "tasks" && hiddenTaskFields.has(field.name)));
    if (entityName !== "tasks") return fields;
    return [
      ...fields.slice(0, 1),
      { name: "linked_contact_id", label: "Contact", type: "select" as const },
      ...fields.slice(1),
    ];
  }, [entity.fields, entityName]);

  function startEdit(record: CrmRecord): void {
    setEditing(record);
    if (entityName === "contacts") setSelectedContact(record);
    const next = Object.fromEntries(entity.fields.map((field) => [field.name, recordValue(record, field.name)]));
    if (entityName === "tasks") {
      next.linked_contact_id = ["contacts", "contact"].includes(recordValue(record, "related_type"))
        ? recordValue(record, "related_id")
        : "";
    }
    setForm(next);
    setFormOpen(true);
  }

  function selectContact(record: CrmRecord): void {
    setSelectedContact(record);
    setDetailOpen(true);
  }

  function startCreate(): void {
    setEditing(null);
    setForm(emptyForm(entityName, entity));
    setFormOpen(true);
  }

  function submit(event: FormEvent): void {
    event.preventDefault();
    save.mutate();
  }

  const rows = records.data?.rows ?? [];
  const isContacts = entityName === "contacts";

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{entity.pluralLabel}</h1>
          <p className="text-sm text-muted-foreground">{entityDescription(entityName)}</p>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input className="pl-9" placeholder="Search" value={query} onChange={(event) => setQuery(event.target.value)} />
          </div>
          <Button type="button" onClick={startCreate}>
            <Plus className="h-4 w-4" />
            New {entity.label}
          </Button>
        </div>
      </div>

      {isContacts && (
        <PipelineBoard
          contacts={rows}
          selectedId={selectedContact?.id}
          onSelect={selectContact}
        />
      )}

      <Card>
        <CardContent className="overflow-x-auto p-0">
          <table className="w-full min-w-[860px] text-sm">
            <thead className="border-b bg-muted/60 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-3">ID</th>
                {visibleColumns.map((field) => <th key={field.name} className="px-4 py-3">{field.label}</th>)}
                <th className="px-4 py-3">Updated</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((record) => (
                <tr
                  key={record.id}
                  className={cn("border-b last:border-0", isContacts && "cursor-pointer hover:bg-muted/30")}
                  onClick={() => {
                    if (isContacts) selectContact(record);
                  }}
                >
                  <td className="px-4 py-3 font-medium">{record.id}</td>
                  {visibleColumns.map((field) => {
                    const raw = recordValue(record, field.name);
                    return (
                      <td key={field.name} className="max-w-56 truncate px-4 py-3">
                        {field.name === "status" && isContacts ? (
                          <span className={cn("rounded-full border px-2 py-1 text-xs font-semibold", stageMeta(raw).tone)}>
                            {stageMeta(raw).label}
                          </span>
                        ) : field.name === "due_date" ? (
                          <span className={cn("rounded-full border px-2 py-1 text-xs font-semibold", dueTone(raw, record.status))}>
                            {dueLabel(raw, record.status)}
                          </span>
                        ) : field.name === "estimated_value" ? formatMoney(raw) : raw}
                      </td>
                    );
                  })}
                  <td className="px-4 py-3 text-muted-foreground">{String(record.updated_at ?? "")}</td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          startEdit(record);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          remove.mutate(record.id);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className="px-4 py-10 text-center text-muted-foreground" colSpan={visibleColumns.length + 3}>
                    No records yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {entityName === "tasks" && (
        <div className="rounded-md border bg-white px-4 py-3 text-sm text-muted-foreground">
          <div className="mb-1 flex items-center gap-2 font-medium text-foreground">
            <ClipboardList className="h-4 w-4" />
            Linked follow-ups
          </div>
          Tasks can be tied to a lead using the Contact field. The sales team sees the customer name, not internal IDs.
        </div>
      )}

      {records.error && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="mr-2 inline h-4 w-4" />
          {records.error.message}
        </div>
      )}

      {formOpen && (
        <Modal
          title={editing ? `Edit ${entity.label}` : `New ${entity.label}`}
          onClose={() => {
            setFormOpen(false);
            setEditing(null);
            setForm(emptyForm(entityName, entity));
          }}
        >
          <form className="space-y-5" onSubmit={submit}>
            <div className="grid gap-4 md:grid-cols-2">
              {formFields.map((field) => (
                <label
                  key={field.name}
                  className={cn("block space-y-2 text-sm font-medium", field.type === "textarea" && "md:col-span-2")}
                >
                  <span>{field.label}{field.required ? " *" : ""}</span>
                  <FieldInput
                    field={field}
                    value={form[field.name] ?? ""}
                    teamMembers={activeTeamMembers}
                    contacts={contactRows}
                    onChange={(value) => setForm((current) => ({ ...current, [field.name]: value }))}
                  />
                </label>
              ))}
            </div>
            {save.error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{save.error.message}</p>}
            <div className="flex justify-end gap-2 border-t pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setFormOpen(false);
                  setEditing(null);
                  setForm(emptyForm(entityName, entity));
                }}
              >
                Cancel
              </Button>
              <Button disabled={save.isPending}>
                {editing ? <CheckCircle2 className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {editing ? "Save" : "Create"}
              </Button>
            </div>
          </form>
        </Modal>
      )}

      {isContacts && selectedContact && detailOpen && (
        <Modal title={recordValue(selectedContact, "name")} onClose={() => setDetailOpen(false)} size="large">
          <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
            <div className="space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className={cn("rounded-full border px-2 py-1 text-xs font-semibold", stageMeta(recordValue(selectedContact, "status")).tone)}>
                  {stageMeta(recordValue(selectedContact, "status")).label}
                </span>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setDetailOpen(false);
                    startEdit(selectedContact);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                  Edit lead
                </Button>
              </div>

              <div className="grid gap-3 text-sm sm:grid-cols-2">
                <div className="flex items-center gap-2"><Phone className="h-4 w-4 text-muted-foreground" />{recordValue(selectedContact, "phone") || "-"}</div>
                <div className="flex items-center gap-2"><UserRound className="h-4 w-4 text-muted-foreground" />{recordValue(selectedContact, "assigned_to") || "Unassigned"}</div>
                <div className="flex items-center gap-2"><MapPin className="h-4 w-4 text-muted-foreground" />{recordValue(selectedContact, "location") || "-"}</div>
                <div className="flex items-center gap-2"><CircleDollarSign className="h-4 w-4 text-muted-foreground" />{formatMoney(recordValue(selectedContact, "estimated_value"))}</div>
              </div>

              <div className="rounded-md bg-muted/50 p-3 text-sm">
                <div className="mb-1 text-xs font-semibold uppercase text-muted-foreground">Requirement</div>
                <div>{recordValue(selectedContact, "requirement") || "No requirement notes yet."}</div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-semibold">Follow-ups</h3>
                  <span className="text-xs text-muted-foreground">{contactSummary.data?.tasks.length ?? 0} open or recent</span>
                </div>
                <div className="space-y-2">
                  {(contactSummary.data?.tasks ?? []).slice(0, 8).map((task) => (
                    <div key={task.id} className="rounded-md border px-3 py-2 text-sm">
                      <div className="font-medium">{recordValue(task, "title")}</div>
                      <div className="mt-1 flex flex-wrap gap-2 text-xs">
                        <span className={cn("rounded-full border px-2 py-0.5", dueTone(task.due_date, task.status))}>
                          {dueLabel(task.due_date, task.status)}
                        </span>
                        <span className="rounded-full border bg-white px-2 py-0.5">{recordValue(task, "assignee") || "Unassigned"}</span>
                      </div>
                    </div>
                  ))}
                  {contactSummary.data?.tasks.length === 0 && (
                    <div className="rounded-md border border-dashed px-3 py-5 text-center text-sm text-muted-foreground">
                      No follow-ups linked yet.
                    </div>
                  )}
                </div>
              </div>

              <div className="space-y-2">
                <h3 className="text-sm font-semibold">Notes</h3>
                {(contactSummary.data?.notes ?? []).slice(0, 6).map((note) => (
                  <div key={note.id} className="rounded-md border px-3 py-2 text-sm">
                    <div className="font-medium">{recordValue(note, "title")}</div>
                    <div className="mt-1 text-muted-foreground">{recordValue(note, "body") || "-"}</div>
                  </div>
                ))}
                {contactSummary.data?.notes.length === 0 && (
                  <div className="rounded-md border border-dashed px-3 py-5 text-center text-sm text-muted-foreground">
                    No notes linked yet.
                  </div>
                )}
              </div>
            </div>

            <form
              className="space-y-3 rounded-md border bg-white p-3"
              onSubmit={(event) => {
                event.preventDefault();
                createFollowUp.mutate();
              }}
            >
              <div className="text-sm font-semibold">Add follow-up</div>
              <Input
                value={followUp.title}
                onChange={(event) => setFollowUp((current) => ({ ...current, title: event.target.value }))}
                placeholder="Task title"
                required
              />
              <div className="grid grid-cols-2 gap-2">
                <Input
                  type="date"
                  value={followUp.due_date}
                  onChange={(event) => setFollowUp((current) => ({ ...current, due_date: event.target.value }))}
                />
                <select
                  className="h-9 w-full rounded-md border bg-white px-3 text-sm outline-none focus:ring-2 focus:ring-ring"
                  value={followUp.priority}
                  onChange={(event) => setFollowUp((current) => ({ ...current, priority: event.target.value }))}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <FieldInput
                field={{ name: "assignee", label: "Assignee", type: "text" }}
                value={followUp.assignee}
                teamMembers={activeTeamMembers}
                contacts={contactRows}
                onChange={(value) => setFollowUp((current) => ({ ...current, assignee: value }))}
              />
              {createFollowUp.error && <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{createFollowUp.error.message}</p>}
              <Button disabled={createFollowUp.isPending} className="w-full">
                <CalendarClock className="h-4 w-4" />
                Add follow-up
              </Button>
            </form>
          </div>
        </Modal>
      )}
    </div>
  );
}
